import React, { useEffect, useState, useContext, useMemo } from 'react'
import { Tooltip } from '@mui/material'
import { Search } from '@mui/icons-material'
import { M_InputTextField, M_ElementDrawer, M_Table, BufferService, filtersSortService, LoadingContext } from '../../M_Components'
import { useTranslation } from 'react-i18next'
import { QueryEntities } from '../../../App'

export function M_DrawerTableTextField({
    label, value, entity, filterField, fieldType, tableLabel, color,
    secondColor, FILTER, required, tooltipAdd, disabled, labelFields,
    onSubmitValue, size, icon, valueIdField, multiple, tableMaxRows,
    filtersOnTable, removeHeaders, inputFilter, disabledBorderColor,
    valueField, height, getOptionsParams, query }) {

    const { t } = useTranslation()
    const [valueInput, setValueInput] = useState(value)
    const [onOpen, setOnOpen] = useState(false)
    const [options, setOptions] = useState([])
    const [selectedRows, setSelectedRows] = useState([])
    const [enabledFetch, setEnabledFetch] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const headerSize = document.body.getElementsByClassName('p-datatable-header')[0]?.offsetHeight || 60
    const paginatorSize = document.body.getElementsByClassName('paginator')[0]?.offsetHeight || 60
    const tabSize = 64
    const tempSize = (document.body.offsetHeight - (headerSize + paginatorSize + tabSize + 5))
    const entityService = new BufferService(entity)
    const filterService = new filtersSortService()
    const ctx = useContext(LoadingContext)

    color = color || ctx.primaryColor || 'var(--color-black)'

    useEffect(() => {
        setSelectedRows([])
    }, [value])

    useEffect(() => {
        if (value !== valueInput) {
            setValueInput(value)
        }
    }, [value, valueInput])

    useEffect(() => {
        if (!searchValue) return

        const delay = setTimeout(() => {
            getValues(searchValue)
        }, 400)

        return () => clearTimeout(delay)
    }, [searchValue])

    const openDrawer = () => {
        setEnabledFetch(true)
        setOnOpen(true)
    }

    const closeDrawer = () => {
        setOnOpen(false)
    }

    const normalizeQuery = (value) => {
        const trimmed = value?.trim()
        if (!trimmed) return ''

        return trimmed.replace(/^[?&]+/, '')
    }

    const splitQuery = (value) => {
        const normalizedQuery = normalizeQuery(value)

        if (!normalizedQuery) {
            return { params: '', filter: '' }
        }

        const parts = normalizedQuery
            .split('&')
            .map(part => part.trim())
            .filter(Boolean)

        const params = []
        const filters = []

        parts.forEach(part => {
            if (part.startsWith('filter=') || part.startsWith('$filter=')) {
                filters.push(part.split('=').slice(1).join('='))
                return
            }

            if (
                part.includes(' eq ') ||
                part.includes(' ne ') ||
                part.includes(' gt ') ||
                part.includes(' ge ') ||
                part.includes(' lt ') ||
                part.includes(' le ') ||
                part.includes(' contains(')
            ) {
                filters.push(part)
                return
            }

            params.push(part)
        })

        return {
            params: params.join('&'),
            filter: filters.join(' and ')
        }
    }

    const keyDown = (e) => {
        if (e.key === 'F4') {
            openDrawer()
        }
    }

    const getLine = (e) => {
        if (multiple) {
            setSelectedRows(e)
        } else {
            closeDrawer()
            onChangeValue(null, e[0], 'changevalue')
        }
    }
    const onSave = () => {
        onSubmitValue(selectedRows)
        closeDrawer()
    }

    const returnTable = () => {
        return (
            <>
                <h2 style={{ marginLeft: '10px', marginTop: '0px', marginBottom: '10px' }}>{t(tableLabel)}</h2>
                <M_Table
                    inputFilter={inputFilter}
                    removeHeaders={removeHeaders}
                    filtersOnTable={filtersOnTable}
                    maxRows={tableMaxRows}
                    tableHeight={tempSize}
                    entityName={entity}
                    useSessionStorage={false}
                    crud={false}
                    onPassLine={getLine}
                    selectSingle={multiple ? false : true}
                    FILTER={FILTER}
                    value={value}
                    valueIdField={valueIdField}
                    multiple={multiple}
                    extraButtons={multiple && [{ label: t("Save"), element: 'default', icon: <img src="./icons/svgsDaNet/save.svg" style={{ filter: 'invert(42%) sepia(44%) saturate(4434%) hue-rotate(74deg) brightness(97%) contrast(101%)', width: '24px' }} alt="save" />, command: () => onSave() }]}
                    valueField={valueField}
                    getOptionsParams={getOptionsParams}
                    query={query}
                />
            </>
        )
    }

    const getValues = (input) => {
        if (!filterField || !input) return
        const fields = Array.isArray(filterField)
            ? filterField
            : [filterField]

        const { params, filter: queryFilter } = splitQuery(query)
        let filterString = `?page=1&pagesize=5`

        if (params) {
            filterString += `&${params}`
        }

        const filtersToApply = []

        fields.forEach((field, index) => {
            let currentFilter = ''
            if (QueryEntities.some(x => x.toLowerCase() === entity.toLowerCase())) {
                currentFilter = `contains(tolower(${field}), '${encodeURIComponent(input.toLowerCase())}')`
            } else {
                currentFilter = `contains(${field}, '${encodeURIComponent(input)}')`
            }

            if (index === 0) {
                filtersToApply.push(currentFilter)
            } else {
                filtersToApply[0] += ` or ${currentFilter}`
            }
        })

        if (queryFilter) {
            filtersToApply.push(queryFilter)
        }

        if (FILTER) {
            filtersToApply.push(FILTER)
        }

        if (filtersToApply.length) {
            filterString += `&filter=${filtersToApply.join(' and ')}`
        }

        entityService.filter(filterString).then(res => {
            if (res.name === 'AxiosError') return
            setOptions(res.Items)
        })
    }


    const onChangeValue = (event, value, reason) => {
        setValueInput(value)

        if (!multiple) {
            onSubmitValue(value)
        }
    }


    const clickOption = (option) => {
        if (multiple) {
            setSelectedRows(prev => {
                const exists = prev.some(p => p[valueField] === option[valueField])
                return exists
                    ? prev.filter(p => p[valueField] !== option[valueField])
                    : [...prev, option]
            })
        } else {
            getLine([option])
        }
    }

    const optionsWithSelected = useMemo(() => {
        const baseOptions = Array.isArray(options) ? options : []

        if (!valueInput || multiple || typeof valueInput !== 'object') {
            return baseOptions
        }

        const hasSameReference = baseOptions.some((opt) => opt === valueInput)
        return hasSameReference ? baseOptions : [valueInput, ...baseOptions]
    }, [options, valueInput, multiple])


    return (
        <>
            <M_InputTextField
                disabledBorderColor={disabledBorderColor}
                autocomplete
                options={optionsWithSelected}
                disabled={disabled}
                label={label}
                value={valueInput}
                onClickOption={clickOption}
                labelFields={labelFields}
                onChangeValue={onChangeValue}
                onChange={(e) => {
                    setSearchValue(e.target.value)
                }}
                onKeyDown={(e) => keyDown(e)}
                color={color}
                secondColor={secondColor}
                required={required}
                size={size}
                height={height}
                InputProps={{
                    startAdornment: (
                        disabled ? <></> :
                            <Tooltip style={{ color: color, cursor: 'pointer' }} title={tooltipAdd ? tooltipAdd : ""} onClick={() => openDrawer()}>
                                {icon ? icon : <Search />}
                            </Tooltip>
                    ),
                }}
            />
            <M_ElementDrawer
                open={onOpen}
                setOpen={setOnOpen}
                onClose={closeDrawer}
                element={enabledFetch ? returnTable() : null}
            />
        </>
    )
}
